import { jsx, jsxs } from "react/jsx-runtime";
import { useClassNameFactory, useColorScheme, ColorSchemeContext } from "@contember/react-utils";
import { deprecate, fallback, dataAttribute, contentThemeClassName, controlsThemeClassName, colorSchemeClassName } from "@contember/utilities";
import { memo, forwardRef, createElement } from "react";
import { Spinner } from "../../Spinner/Spinner.js";
import { Text } from "../../Typography/Text.js";
const AnchorButton = memo(forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(BaseButton, { ...props, ref, Component: "a" });
}));
AnchorButton.displayName = "Interface.AnchorButton";
const Button = memo(forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(BaseButton, { ...props, ref, Component: "button" });
}));
Button.displayName = "Interface.Button";
const BaseButton = memo(forwardRef(({
  Component,
  active,
  align,
  bland,
  borderRadius = true,
  className: classNameProp,
  componentClassName = "button",
  display = "inline",
  children,
  disabled,
  distinction,
  elevated = false,
  elevation,
  flow,
  focusRing = true,
  intent,
  type,
  justification,
  justify = "center",
  loading,
  inset,
  padding = true,
  scheme: schemeProp,
  size,
  square = false,
  ...rest
}, ref) => {
  deprecate("1.3.0", bland !== void 0, "`bland` prop", "`distinction` prop");
  deprecate("1.3.0", flow === "squarish", 'flow="squarish"', "`square={true}`");
  deprecate("1.3.0", flow === "circular", 'flow="circular"', '`square={true} borderRadius="full"`');
  square = fallback(square, flow === "squarish" || flow === "circular", true);
  borderRadius = fallback(borderRadius, flow === "circular", "full");
  deprecate("1.3.0", flow === "default", '`flow="default"`', "omitted `flow` prop");
  deprecate("1.3.0", flow === "block", '`flow="block"`', '`display="block"`');
  deprecate("1.3.0", flow === "generous", '`flow="generous"`', '`padding="large"`');
  deprecate("1.3.0", flow === "generousBlock", '`flow="generousBlock"`', '`display="block" padding="large"`');
  display = fallback(display, flow === "block" || flow === "generousBlock", "block");
  padding = fallback(padding, flow === "generous" || flow === "generousBlock", "padding");
  deprecate("1.3.0", typeof elevated !== "boolean", "`elevate` prop", "`elevate` prop");
  elevated = fallback(elevated, typeof elevation !== "boolean", elevation === "default" ? true : false);
  deprecate("1.3.0", justification !== void 0, "`justification` prop", "`justify` prop");
  justify = fallback(justify, justification !== void 0, { default: "center", justifyStart: "start", justifyCenter: "center", justifyEnd: "end" }[justification ?? "default"]);
  deprecate("1.3.0", padding === "small", 'padding="small"', 'padding="gap"');
  padding = fallback(padding, padding === "small", "gap");
  deprecate("1.3.0", distinction === "default", '`distinction="default"`', "omitted `distinction` prop");
  distinction = fallback(distinction, distinction === "default", void 0);
  if (disabled === true) {
    rest["aria-disabled"] = true;
    rest["tabIndex"] = -1;
  }
  const themeIntent = !disabled ? intent : "default";
  const className = useClassNameFactory(componentClassName);
  const colorScheme = useColorScheme();
  const scheme = schemeProp ?? colorScheme;
  return createElement(Component, {
    ...rest,
    "data-active": dataAttribute(active),
    "data-align": dataAttribute(align),
    // TODO: deprecated since v1.3.0
    "data-bland": dataAttribute(bland),
    "data-border-radius": dataAttribute(borderRadius),
    "data-disabled": dataAttribute(disabled),
    "data-display": dataAttribute(display),
    "data-distinction": dataAttribute(distinction),
    "data-elevated": dataAttribute(elevated),
    "data-focus-ring": dataAttribute(focusRing),
    "data-inset": dataAttribute(inset ?? (distinction === "seamless" ? true : void 0)),
    "data-intent": dataAttribute(intent),
    "data-justify": dataAttribute(justify),
    "data-loading": dataAttribute(loading),
    "data-padding": dataAttribute(padding),
    "data-size": dataAttribute(size),
    "data-square": dataAttribute(square),
    disabled,
    "className": className(null, [
      contentThemeClassName(distinction === "default" ? null : themeIntent),
      controlsThemeClassName(themeIntent),
      colorSchemeClassName(scheme),
      classNameProp
    ]),
    "type": type ?? (Component === "button" ? "button" : void 0),
    ref,
    ...disabled ? {
      href: null,
      onClick: null
    } : void 0
  }, /* @__PURE__ */ jsxs(ColorSchemeContext.Provider, { value: scheme, children: [
    /* @__PURE__ */ jsx("div", { className: className("content"), children: typeof children === "string" || typeof children === "number" ? /* @__PURE__ */ jsx(Text, { children: `${children}` }) : children }),
    loading && /* @__PURE__ */ jsx("span", { className: className("spinner"), children: /* @__PURE__ */ jsx(Spinner, {}) })
  ] }));
}));
BaseButton.displayName = "Interface.BaseButton";
export {
  AnchorButton,
  BaseButton,
  Button
};
//# sourceMappingURL=Button.js.map
